<template>
  <div class="operator-group-view-container px-8">
    <FlyoutMenuHead :selectedItem="selectedItem" type="operator_group"/>
    
    <!-- Main Content -->
    <div class="operator-group-content">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" size="48" />
        <p class="mt-4 text-h6">Loading operator group...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-8">
        <v-icon size="64" color="error">mdi-alert-circle</v-icon>
        <h3 class="text-h6 mt-3 error--text">Failed to load operator group</h3>
        <p class="text-body-2 mt-2">{{ error.message || error }}</p>
        <v-btn color="primary" @click="loadOperatorGroup" class="mt-4">
          Retry
        </v-btn>
      </div>

      <!-- Group Details -->
      <template v-else-if="operatorGroup">
        <!-- Main Info Card -->
        <v-card class="panel-card" flat>
          <v-card-text class="panel-inner">
            <div class="section-title">Main info</div>

            <v-row class="mt-4">
              <v-col cols="12" md="6">
                <div class="field">
                  <div class="field-label">Group name</div>
                  <div class="field-value">{{ operatorGroup.name || '—' }}</div>
                </div>

                <div class="field mt-6">
                  <div class="field-label">Group type</div>
                  <div class="field-value">
                    <v-chip 
                      :color="operatorGroup.type === 'smart' ? 'purple' : 'blue'" 
                      small 
                      outlined
                    >
                      {{ operatorGroup.type || '—' }}
                    </v-chip>
                  </div>
                </div>

                <div class="field mt-6">
                  <div class="field-label">Automatic group</div>
                  <div class="field-value">
                    <v-chip 
                      :color="operatorGroup.automatic_operator_group ? 'orange' : 'grey'" 
                      small 
                      outlined
                    >
                      {{ operatorGroup.automatic_operator_group ? 'Yes' : 'No' }}
                    </v-chip>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="field">
                  <div class="field-label">Status</div>
                  <div class="field-value">
                    <v-chip 
                      :color="operatorGroup.active ? 'success' : 'error'" 
                      small 
                      outlined
                    >
                      {{ operatorGroup.active ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </div>
                </div>

                <div class="field mt-6">
                  <div class="field-label">Number of operators</div>
                  <div class="field-value">{{ operatorGroup.number_of_operators || 0 }}</div>
                </div>

                <div class="field mt-6">
                  <div class="field-label">Space</div>
                  <div class="field-value">{{ operatorGroup.space_name || '—' }}</div>
                </div>
              </v-col>
            </v-row>

            <v-row v-if="operatorGroup.description" class="mt-4">
              <v-col cols="12">
                <div class="field">
                  <div class="field-label">Description</div>
                  <div class="field-value">{{ operatorGroup.description }}</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Additional Details Card -->
        <v-card class="panel-card mt-6" flat>
          <v-card-text class="panel-inner">
            <div class="section-title">Additional details</div>

            <v-row class="mt-4">
              <v-col cols="12" md="6">
                <div class="field">
                  <div class="field-label">Created by</div>
                  <div class="field-value">{{ operatorGroup.created_by_email || '—' }}</div>
                </div>

                <div class="field mt-6">
                  <div class="field-label">Created at</div>
                  <div class="field-value">{{ formattedCreatedAt || '—' }}</div>
                </div>

                <div class="field mt-6">
                  <div class="field-label">Updated at</div>
                  <div class="field-value">{{ formattedUpdatedAt || '—' }}</div>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="field">
                  <div class="field-label">Group UUID</div>
                  <div class="field-value small-value">{{ operatorGroup.uuid || '—' }}</div>
                </div>

                <div class="field mt-6">
                  <div class="field-label">Group ID</div>
                  <div class="field-value">{{ operatorGroup.id || '—' }}</div>
                </div>

                <div class="field mt-6" v-if="operatorGroup.type === 'smart'">
                  <div class="field-label">Smart group rules</div>
                  <div class="field-value">
                    <v-alert type="info" dense outlined class="mb-0">
                      Smart group criteria are managed automatically by the system.
                    </v-alert>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Group Members Card -->
        <v-card class="panel-card mt-6" flat>
          <v-card-text class="panel-inner">
            <div class="section-title">Group members</div>

            <div v-if="loadingMembers" class="text-center py-4">
              <v-progress-circular indeterminate color="primary" size="24" />
              <span class="ml-3">Loading members...</span>
            </div>

            <div v-else-if="groupMembers.length === 0" class="text-center py-8">
              <v-icon size="48" color="grey lighten-1">mdi-account-group-outline</v-icon>
              <h4 class="text-h6 mt-3 grey--text">No operators in this group</h4>
              <p class="text-body-2 grey--text">
                This group currently has no members assigned.
              </p>
            </div>

            <div v-else>
              <v-list dense>
                <v-list-item
                  v-for="member in groupMembers"
                  :key="member.uuid"
                  class="member-item"
                >
                  <v-list-item-avatar>
                    <v-avatar color="grey lighten-2" size="32">
                      <span class="text-caption font-weight-bold">
                        {{ getInitials(member.username) }}
                      </span>
                    </v-avatar>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title class="text-body-2 font-weight-medium">
                      {{ member.username }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ member.email }}
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-chip
                      :color="member.enabled ? 'success' : 'error'"
                      x-small
                      outlined
                    >
                      {{ member.enabled ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>

              <div v-if="groupMembersMeta && groupMembersMeta.count > groupMembers.length" class="text-center pt-3">
                <p class="text-caption grey--text">
                  Showing {{ groupMembers.length }} of {{ groupMembersMeta.count }} members
                </p>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FlyoutMenuHead from '../FlyoutMenuHead.vue';
import { mdiAccountGroup } from '@mdi/js';

export default {
  name: 'OperatorGroupView',
  components: {
    FlyoutMenuHead
  },
  data() {
    return {
      localError: null,
      loadingMembers: false,
    };
  },
  computed: {
    ...mapGetters({
      operatorGroup: 'operators/getGroupDetail',
      groupMembers: 'operators/getGroupMembers',
      groupMembersMeta: 'operators/getGroupMembersMeta',
      loadingStates: 'operators/isLoading',
      errors: 'operators/getErrors',
    }),

    operatorGroupUuid() {
      return this.$route.params.uuid;
    },

    loading() {
      return this.loadingStates?.groups || false;
    },

    error() {
      return this.errors?.groups || this.localError;
    },

    formattedCreatedAt() {
      return this.formatDate(this.operatorGroup?.created_at);
    },

    formattedUpdatedAt() {
      return this.formatDate(this.operatorGroup?.updated_at);
    },

    selectedItem() {
      return {
        name: this.operatorGroup?.name || "Operator Group Details",
        subtitle: this.operatorGroup?.active ? 'Active' : 'Inactive',
        statusIcon: mdiAccountGroup,
        statusColor: this.operatorGroup?.active ? 'success' : 'error',
        isActive: this.operatorGroup?.active,
        uuid: this.operatorGroup?.uuid,
        total_number: this.operatorGroup?.number_of_operators || 0
      };
    }
  },

  watch: {
    operatorGroupUuid: {
      handler(newUuid, oldUuid) {
        if (newUuid && newUuid !== oldUuid) {
          this.loadOperatorGroup();
        }
      },
      immediate: false
    }
  },

  async mounted() {
    if (this.operatorGroupUuid) {
      await this.loadOperatorGroup();
    }
  },

  methods: {
    ...mapActions({
      fetchOperatorGroup: 'operators/fetchOperatorGroup',
      fetchOperatorsInGroup: 'operators/fetchOperatorsInGroup'
    }),

    async loadOperatorGroup() {
      this.localError = null;
      try {
        // Load the group details
        await this.fetchOperatorGroup(this.operatorGroupUuid);
        
        // Load group members
        this.loadingMembers = true;
        await this.fetchOperatorsInGroup({ 
          operator_group_uuid: this.operatorGroupUuid,
          qs: 'page_size=50&page=1' // Load first 50 members
        });
      } catch (err) {
        console.error('Failed to load operator group', err);
        this.localError = err;
      } finally {
        this.loadingMembers = false;
      }
    },

    formatDate(value) {
      if (!value) return null;
      try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return null;
        return d.toLocaleString();
      } catch (e) {
        return null;
      }
    },

    getInitials(firstName, lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() : "";
      const last = lastName ? lastName.charAt(0).toUpperCase() : "";
      return first + last || "?";
    }
  }
};
</script>

<style scoped>
.operator-group-view-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: transparent !important;
  color: var(--v-text-base);
}

/* Content area */
.operator-group-content {
  flex: 1;
  overflow-y: auto;
  background: transparent !important;
}

/* Panel/card that matches the screenshot's inset rounded look */
.panel-card {
  background: var(--v-surface-base, #f5f5f5) !important;
  border-radius: 15px !important;
  box-shadow: none !important;
  border: 1px solid var(--v-divider-base, rgba(0,0,0,0.05)) !important;
}

.panel-card .v-card {
  border-radius: 20px !important;
}

/* Dark theme specific styles */
.theme--dark .panel-card {
  background: var(--v-surface-base, #2f3236) !important;
  border: 1px solid rgba(255,255,255,0.03) !important;
}

.panel-inner {
  padding: 22px;
  background: transparent !important;
}

/* Ensure v-card-text has transparent background */
.panel-card .v-card__text {
  background: transparent !important;
}

/* Section title (blue) */
.section-title {
  color: #2ea0ff; /* blue */
  font-weight: 600;
  margin-bottom: 6px;
}

/* Field layout: small label above a larger value */
.field {
  display: flex;
  flex-direction: column;
}

.field-label {
  font-size: 0.85rem;
  color: var(--v-text-secondary);
  margin-bottom: 8px;
  text-transform: none;
}

.field-value {
  font-size: 1rem;
  color: var(--v-text-base);
  font-weight: 500;
  word-break: break-word;
}

/* smaller / muted long values like uuid */
.small-value {
  font-size: 0.85rem;
  color: var(--v-text-secondary);
}

/* Member item styling */
.member-item {
  border-bottom: 1px solid var(--v-divider-base);
}

.member-item:last-child {
  border-bottom: none;
}

/* spacing helpers */
.mt-4 { margin-top: 16px; }
.mt-6 { margin-top: 24px; }

/* Responsive tweaks */
@media (max-width: 960px) {
  .operator-group-content {
    padding: 16px;
  }
}
</style>
